import { useEffect, useRef } from 'react';
import { CircleMarker, Popup, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import '@/lib/leaflet-global'; // Make L global BEFORE leaflet-rotate
import 'leaflet-rotate';
import 'leaflet.markercluster';
import 'leaflet-routing-machine';
import { toast } from 'sonner';
import { MARKER_COLORS, RESOURCE_CATEGORIES } from './constants';

// EnableRotation — Forces leaflet-rotate to be active on this map instance.
// react-leaflet may not pass the `rotate` option through to L.Map, so we
// directly patch the flag if needed. leaflet-rotate already patched all the
// math functions (latLngToContainerPoint etc.), they just check this._rotate.
export const EnableRotation = () => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    console.log('[EnableRotation] map._rotate:', map._rotate, 'map._rotatePane:', !!map._rotatePane, 'options.rotate:', map.options?.rotate, 'setBearing:', typeof map.setBearing);
    if (!map._rotate) {
      // Rotation wasn't enabled at init — force it now
      console.warn('[EnableRotation] Rotation was NOT enabled at init. Forcing...');
      map.options.rotate = true;
      map._rotate = true;
      map._bearing = 0;
      // Create rotate pane if missing
      if (!map._rotatePane && map.createPane) {
        try {
          map._rotatePane = map.createPane('rotatePane', map._mapPane);
          map._rotatePanePos = L.point(0, 0);
          // Move existing panes under the rotate pane
          const paneNames = ['tilePane', 'overlayPane', 'shadowPane', 'markerPane', 'tooltipPane', 'popupPane'];
          paneNames.forEach(name => {
            const pane = map.getPane(name);
            if (pane && pane.parentNode === map._mapPane) {
              map._rotatePane.appendChild(pane);
            }
          });
          console.log('[EnableRotation] Created _rotatePane and moved child panes');
        } catch (e) {
          console.error('[EnableRotation] Failed to create rotate pane:', e);
        }
      }
    }
    // Verify rotation works (no bearing change — just check for errors)
    try {
      const current = map.getBearing ? map.getBearing() : 0;
      console.log('[EnableRotation] Current bearing:', current, 'setBearing test: OK');
    } catch (e) {
      console.error('[EnableRotation] setBearing test FAILED:', e);
    }
  }, [map]);
  return null;
};


// TouchFix — Creates an invisible interactive rectangle in the overlay pane
// to ensure ALL touches are captured by Leaflet's event system.
// Fixes Samsung Internet (and some mobile browsers) where touches on "empty"
// map areas don't reach the Leaflet container's event handlers.
export const TouchFix = () => {
  const map = useMap();
  const rectRef = useRef(null);

  useEffect(() => {
    if (!map) return;
    // Create a huge invisible rectangle that covers the entire world
    rectRef.current = L.rectangle(
      [[-90, -180], [90, 180]],
      { 
        fillOpacity: 0, 
        weight: 0, 
        opacity: 0, 
        interactive: true,
        bubblingMouseEvents: true,
      }
    ).addTo(map);

    return () => {
      if (rectRef.current) {
        try { rectRef.current.remove(); } catch {}
        rectRef.current = null;
      }
    };
  }, [map]);

  return null;
};


// Map auto-recenter — only when location actually changes, never fights user zoom/pan
export const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  const prevRef = useRef(null);
  const initialRef = useRef(false);

  useEffect(() => {
    if (!center || !Number.isFinite(center[0]) || !Number.isFinite(center[1])) return;

    // Check if center actually changed (compare values, not array reference)
    const prev = prevRef.current;
    if (prev && Math.abs(prev[0] - center[0]) < 0.0001 && Math.abs(prev[1] - center[1]) < 0.0001) {
      return; // Same location — don't reset the view
    }
    prevRef.current = [center[0], center[1]];

    // Guard: Leaflet flyTo crashes if the container has 0 dimensions
    const size = map.getSize();
    if (!size || size.x === 0 || size.y === 0) {
      const onResize = () => {
        const s = map.getSize();
        if (s && s.x > 0 && s.y > 0) {
          map.off('resize', onResize);
          try { map.setView(center, zoom, { animate: false }); } catch {}
        }
      };
      map.on('resize', onResize);
      const t = setTimeout(() => { map.off('resize', onResize); try { map.setView(center, zoom, { animate: false }); } catch {} }, 300);
      return () => { clearTimeout(t); map.off('resize', onResize); };
    }

    // First mount: setView without animation; subsequent: flyTo but keep user's zoom
    if (!initialRef.current) {
      initialRef.current = true;
      map.setView(center, zoom, { animate: false });
    } else {
      map.flyTo(center, map.getZoom(), { duration: 1.2 });
    }
  }, [map, center, zoom]);
  return null;
};

// Leaflet cluster layer (imperative) — optimized for 70K+ markers
export const ClusterLayer = ({ resources, onGetDirections }) => {
  const map = useMap();

  useEffect(() => {
    const cluster = L.markerClusterGroup({
      maxClusterRadius: 120,          // aggressive clustering at wide zooms
      chunkedLoading: true,           // don't block the main thread
      chunkInterval: 100,
      chunkDelay: 20,
      disableClusteringAtZoom: 16,    // only show individual pins when very zoomed in
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      removeOutsideVisibleBounds: true,
      animate: false,                 // skip cluster animations on mobile
      iconCreateFunction: (c) => {
        const count = c.getChildCount();
        const size = count < 10 ? 36 : count < 100 ? 44 : count < 500 ? 52 : 60;
        return L.divIcon({
          html: `<div style="background:rgba(168,85,247,0.85);color:#fff;border-radius:50%;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;font-size:${size < 44 ? 12 : 14}px;font-weight:700;border:2px solid rgba(255,255,255,0.3);box-shadow:0 2px 8px rgba(0,0,0,0.4)">${count >= 1000 ? Math.round(count/1000)+'k' : count}</div>`,
          className: '',
          iconSize: [size, size],
        });
      },
    });

    resources.forEach((r) => {
      const lat = parseFloat(r.lat || r.latitude);
      const lng = parseFloat(r.lng || r.longitude);
      if (isNaN(lat) || isNaN(lng)) return;
      const color = MARKER_COLORS[r.category] || MARKER_COLORS.other;
      const marker = L.circleMarker([lat, lng], {
        radius: 7,
        fillColor: color,
        color: '#fff',
        weight: 1.5,
        fillOpacity: 0.9,
      });
      const catLabel = RESOURCE_CATEGORIES.find(c => c.value === r.category)?.label || r.category || '';
      let html = `<div style="min-width:180px;max-width:260px;font-family:Manrope,sans-serif">`;
      html += `<p style="font-weight:700;font-size:13px;margin:0 0 3px;color:#fff">${r.name || 'Resource'}</p>`;
      html += `<p style="font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:${color};margin:0 0 4px;font-weight:600">${catLabel}</p>`;
      if (r.address) html += `<p style="font-size:11px;color:#ccc;margin:0 0 2px">${r.address}</p>`;
      if (r.hours) html += `<p style="font-size:10px;color:#999;margin:0 0 2px">${r.hours}</p>`;
      if (r.description) html += `<p style="font-size:10px;color:#aaa;margin:3px 0 0">${r.description}</p>`;
      html += `<button class="crisis-directions-btn" data-lat="${lat}" data-lng="${lng}" data-name="${(r.name || 'Resource').replace(/"/g, '&quot;')}" style="margin-top:6px;width:100%;padding:5px 0;background:#a855f7;color:#fff;border:none;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer;letter-spacing:0.3px">Get Directions</button>`;
      html += `</div>`;
      marker.bindPopup(html, { className: 'crisis-marker-popup' });
      cluster.addLayer(marker);
    });

    map.addLayer(cluster);

    const handleDirectionsClick = (e) => {
      const btn = e.target.closest('.crisis-directions-btn');
      if (btn && onGetDirections) {
        onGetDirections({
          lat: parseFloat(btn.dataset.lat),
          lng: parseFloat(btn.dataset.lng),
          name: btn.dataset.name,
        });
        map.closePopup();
      }
    };
    map.getContainer().addEventListener('click', handleDirectionsClick);

    return () => {
      try { map.getContainer().removeEventListener('click', handleDirectionsClick); } catch {}
      try { map.removeLayer(cluster); } catch {}
    };
  }, [map, resources, onGetDirections]);

  return null;
};

// Coordinate validity helper
const isValidPos = (p) => p != null && Number.isFinite(p.lat) && Number.isFinite(p.lng);

// User location — Blue dot marker, position via DOM events
export const UserLocationMarker = ({ position, accuracy }) => {
  const map = useMap();
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const headingRef = useRef(0);
  const mapBearingRef = useRef(0);

  // Build icon with directional chevron — chevron always points UP on screen
  // because the map rotates to put the heading direction at top
  const buildIcon = (showChevron = false) => {
    const chevronSvg = showChevron
      ? `<polygon points="14,0 6,14 14,10 22,14" fill="#1a73e8" opacity="0.5"/>`
      : '';
    return L.divIcon({
      html: `<div style="width:28px;height:28px;position:relative;pointer-events:none" class="user-dot-icon">
        <svg viewBox="0 0 28 28" width="28" height="28">
          ${chevronSvg}
          <circle cx="14" cy="14" r="13" fill="#1a73e8" opacity="0.15" class="user-dot-pulse"/>
          <circle cx="14" cy="14" r="8" fill="#1a73e8" stroke="#fff" stroke-width="2.5"/>
          <circle cx="12" cy="12" r="2" fill="#fff" opacity="0.35"/>
        </svg>
      </div>`,
      className: 'user-location-marker',
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
  };

  useEffect(() => {
    if (!isValidPos(position)) return;
    const pos = [position.lat, position.lng];
    if (!markerRef.current) {
      markerRef.current = L.marker(pos, { icon: buildIcon(false), zIndexOffset: 1100 }).addTo(map);
    } else {
      markerRef.current.setLatLng(pos);
    }
    const acc = Math.min(accuracy || 50, 30); // Cap at 30m to avoid visual clutter
    if (!circleRef.current) {
      circleRef.current = L.circle(pos, {
        radius: acc, color: '#1a73e8', fillColor: '#1a73e8',
        fillOpacity: 0.06, weight: 1, opacity: 0.15,
      }).addTo(map);
    } else {
      circleRef.current.setLatLng(pos);
      circleRef.current.setRadius(acc);
    }
  }, [map, position, accuracy]);

  // Listen for compass heading → show directional chevron
  useEffect(() => {
    let hasChevron = false;
    const handler = (e) => {
      headingRef.current = e.detail.heading;
      // Show chevron once we get compass data
      if (!hasChevron && markerRef.current) {
        hasChevron = true;
        markerRef.current.setIcon(buildIcon(true));
      }
    };
    window.addEventListener('crisis-heading', handler);
    return () => window.removeEventListener('crisis-heading', handler);
  }, []);

  // Listen for high-frequency GPS updates — smooth animated interpolation like Google Maps
  useEffect(() => {
    let animFrame = null;
    let currentLat = null;
    let currentLng = null;
    let targetLat = null;
    let targetLng = null;
    let animStart = 0;
    const ANIM_DURATION = 400; // ms — fast lerp to keep dot near real position

    const animate = () => {
      if (currentLat === null || targetLat === null) return;
      const elapsed = Date.now() - animStart;
      const t = Math.min(elapsed / ANIM_DURATION, 1);
      // Ease-out cubic for natural deceleration
      const ease = 1 - Math.pow(1 - t, 3);
      const lat = currentLat + (targetLat - currentLat) * ease;
      const lng = currentLng + (targetLng - currentLng) * ease;
      const pos = [lat, lng];
      if (markerRef.current) markerRef.current.setLatLng(pos);
      if (circleRef.current) circleRef.current.setLatLng(pos);
      if (t < 1) {
        animFrame = requestAnimationFrame(animate);
      } else {
        currentLat = targetLat;
        currentLng = targetLng;
      }
    };

    const handler = (e) => {
      const { lat, lng, speed, gpsHeading } = e.detail;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      // Project position slightly ahead using speed + heading to compensate for GPS lag (~1-2s)
      let projLat = lat, projLng = lng;
      const spd = (speed && Number.isFinite(speed) && speed > 1) ? speed : 0;
      if (spd > 1) {
        const headingRad = Number.isFinite(gpsHeading) ? gpsHeading * Math.PI / 180 : null;
        if (headingRad !== null) {
          // Project 2 seconds ahead to compensate for GPS reporting lag
          const projM = spd * 2.0;
          projLat = lat + (Math.cos(headingRad) * projM) / 111000;
          projLng = lng + (Math.sin(headingRad) * projM) / (111000 * Math.cos(lat * Math.PI / 180));
        }
      }

      // First reading — snap immediately
      if (currentLat === null) {
        currentLat = projLat;
        currentLng = projLng;
        targetLat = projLat;
        targetLng = projLng;
        const pos = [projLat, projLng];
        if (markerRef.current) markerRef.current.setLatLng(pos);
        if (circleRef.current) circleRef.current.setLatLng(pos);
        return;
      }
      // Start animating from current interpolated position to new target
      if (animFrame) cancelAnimationFrame(animFrame);
      if (markerRef.current) {
        const cur = markerRef.current.getLatLng();
        currentLat = cur.lat;
        currentLng = cur.lng;
      }
      targetLat = projLat;
      targetLng = projLng;
      animStart = Date.now();
      animFrame = requestAnimationFrame(animate);
    };
    window.addEventListener('crisis-gps', handler);
    return () => {
      window.removeEventListener('crisis-gps', handler);
      if (animFrame) cancelAnimationFrame(animFrame);
    };
  }, []);

  useEffect(() => {
    return () => {
      [markerRef, circleRef].forEach(ref => {
        if (ref.current) { try { ref.current.remove(); } catch {} ref.current = null; }
      });
    };
  }, []);

  return null;
};

// Calculate bearing between two points
function getBearing(from, to) {
  const toRad = (d) => d * Math.PI / 180;
  const toDeg = (r) => r * 180 / Math.PI;
  const dLon = toRad(to.lng - from.lng);
  const y = Math.sin(dLon) * Math.cos(toRad(to.lat));
  const x = Math.cos(toRad(from.lat)) * Math.sin(toRad(to.lat)) - Math.sin(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.cos(dLon);
  const bearing = (toDeg(Math.atan2(y, x)) + 360) % 360;
  const dirs = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
  return dirs[Math.round(bearing / 45) % 8];
}

// Calculate distance in metres between two points (haversine)
function getDistanceMetres(from, to) {
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lng - from.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Voice Directions Routing — uses cached route when offline, falls back to A* then direct-line
export const RoutingControl = ({ from, to, onSteps, onClear, onRouteCoords, onSpeedLimits, transportMode = 'foot', quiet = false }) => {
  const map = useMap();
  const controlRef = useRef(null);

  useEffect(() => {
    if (!from || !to || !Number.isFinite(from.lat) || !Number.isFinite(from.lng) || !Number.isFinite(to.lat) || !Number.isFinite(to.lng)) return;
    if (controlRef.current) {
      try { map.removeControl(controlRef.current); } catch {}
      controlRef.current = null;
    }

    // ── PRIMARY: Offline A* road-by-road routing (uses downloaded graph) ──
    const routeWithOfflineAStar = async () => {
      try {
        const { OfflineRouter } = await import('../../lib/offline-router.js');
        const available = await OfflineRouter.isAvailable();
        if (!available) {
          console.log('[RoutingControl] No offline graph available, skipping A*');
          toast.error('Road graph not downloaded. Go to Blackout Zone settings to download offline data.', { duration: 5000 });
          return false;
        }

        const toastId = quiet ? null : toast.loading('Calculating offline route...', { duration: 30000 });

        const modeMap = { foot: 'walk', car: 'drive', bike: 'cycle', driving: 'drive', cycling: 'cycle' };
        const routeMode = modeMap[transportMode] || 'walk';

        console.log(`[RoutingControl] A* routing: ${from.lat.toFixed(5)},${from.lng.toFixed(5)} → ${to.lat.toFixed(5)},${to.lng.toFixed(5)} mode=${routeMode}`);

        const result = await OfflineRouter.route(
          { lat: from.lat, lng: from.lng },
          { lat: to.lat, lng: to.lng },
          routeMode
        );

        if (result && result.coordinates?.length > 1) {
          const km = (result.distance / 1000).toFixed(1);
          const mins = Math.round(result.duration / 60);
          if (toastId) toast.success(`Offline route: ${km}km, ~${mins} min`, { id: toastId, duration: 3000 });

          const steps = result.directions.map(d => ({
            text: d.instruction,
            distance: d.distance,
            time: Math.round(d.distance / 1.4),
            type: d.type || 'Offline',
            latLng: { lat: d.lat, lng: d.lng },
          }));
          console.log('[RoutingControl] A* produced', steps.length, 'steps,', result.coordinates.length, 'coords');
          if (onSteps) onSteps(steps, { totalDistance: result.distance, totalTime: result.duration });
          if (onRouteCoords) onRouteCoords(result.coordinates.map(c => ({ lat: c[0], lng: c[1] })));
          try { map.setView([from.lat, from.lng], 17, { animate: true, duration: 0.3 }); } catch {}
          return true;
        }

        console.warn('[RoutingControl] A* found no path — falling back');
        if (toastId) toast.dismiss(toastId);
        return false;
      } catch (err) {
        console.error('[RoutingControl] A* error:', err.message);
        return false;
      }
    };

    // Reroutes: OSRM first (1-2s like Google Maps), A* fallback
    // Initial routes: A* first (works offline), OSRM fallback
    const isReroute = !!to._reroute;
    let unmounted = false;

    const tryOSRM = async () => {
      console.log('[RoutingControl] Trying OSRM online routing');
      let osrmOk = false;
      const profile = transportMode === 'car' ? 'driving' : transportMode === 'bike' ? 'cycling' : 'foot';

      const bearing = to._bearing;
      const hasBearing = Number.isFinite(bearing);
      let osrmUrl = `https://router.project-osrm.org/route/v1/${profile}/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson&steps=true`;
      if (hasBearing) {
        osrmUrl += `&bearings=${Math.round(bearing)},150;`;
      }

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        const r = await fetch(osrmUrl, { signal: controller.signal });
        clearTimeout(timeoutId);
        const data = await r.json();
        if (unmounted) return;

        if (data.routes?.length) {
          const bestRoute = data.routes[0];
          const coords = bestRoute.geometry.coordinates.map(c => ({ lat: c[1], lng: c[0] }));
          if (onRouteCoords) onRouteCoords(coords);

          // Extract speed annotations for speed limit signs
          const speedAnnotations = bestRoute.legs?.[0]?.annotation?.speed;
          if (onSpeedLimits && speedAnnotations?.length) {
            onSpeedLimits({ speeds: speedAnnotations, coords });
          }

          if (bestRoute.legs?.[0]?.steps && onSteps) {
            let coordIdx = 0;
            const steps = bestRoute.legs[0].steps.map(s => {
              const latLng = s.maneuver ? { lat: s.maneuver.location[1], lng: s.maneuver.location[0] } : coords[coordIdx] || null;
              coordIdx += s.geometry?.coordinates?.length || 1;
              const modifier = s.maneuver?.modifier || '';
              const type = s.maneuver?.type || '';
              const road = s.name || '';

              let text;
              if (type === 'depart') {
                text = road ? `Head ${modifier || 'forward'} on ${road}` : `Head ${modifier || 'forward'}`;
              } else if (type === 'arrive') {
                text = 'You have arrived at your destination';
              } else if (type === 'turn') {
                text = road ? `Turn ${modifier} onto ${road}` : `Turn ${modifier}`;
              } else if (type === 'new name' || type === 'continue') {
                text = road ? `Continue onto ${road}` : 'Continue straight';
              } else if (type === 'roundabout' || type === 'rotary') {
                const exit = s.maneuver?.exit ? ` and take exit ${s.maneuver.exit}` : '';
                text = road ? `At the roundabout${exit}, onto ${road}` : `At the roundabout${exit}`;
              } else if (type === 'merge') {
                text = road ? `Merge ${modifier} onto ${road}` : `Merge ${modifier || 'ahead'}`;
              } else if (type === 'fork') {
                text = road ? `Keep ${modifier} onto ${road}` : `Keep ${modifier || 'ahead'}`;
              } else if (type === 'end of road') {
                text = road ? `Turn ${modifier} onto ${road}` : `Turn ${modifier || 'ahead'} at end of road`;
              } else if (type === 'notification') {
                text = road || 'Continue';
              } else {
                text = road ? `${modifier || 'Continue'} onto ${road}` : (modifier || type || 'Continue');
              }
              text = text.charAt(0).toUpperCase() + text.slice(1);
              return { text, distance: s.distance, time: s.duration, type: type || modifier, latLng };
            });
            console.log('[RoutingControl] OSRM:', steps.length, 'steps');
            onSteps(steps, { totalDistance: bestRoute.distance, totalTime: bestRoute.duration });
          }
          try { map.setView([from.lat, from.lng], 17, { animate: true, duration: 0.3 }); } catch {}
          osrmOk = true;
        }
      } catch (err) {
        if (unmounted) return;
        console.warn('[RoutingControl] OSRM failed:', err.message);
      }

      if (unmounted) return osrmOk;
      return osrmOk;
    };

    const startRouting = async () => {
      // Always try OSRM (online) first for best quality routes
      // Falls back to offline A* if OSRM fails/times out (5s)
      const osrmOk = await tryOSRM();
      if (unmounted || osrmOk) return;
      const astarOk = await routeWithOfflineAStar();
      if (unmounted || astarOk) return;

      // Both failed
      if (onSteps) onSteps([{
        text: 'No route available. Check offline data or internet connection.',
        distance: 0, time: 0, type: 'Error', latLng: to
      }], { totalDistance: 0, totalTime: 0 });
    };

    startRouting();

    return () => {
      unmounted = true;
      if (controlRef.current) {
        try { if (controlRef.current._map) controlRef.current.remove(); } catch {}
        controlRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, to, transportMode]);

  return null;
};

// Drop Pin Layer (tap anywhere to navigate)
export const DropPinLayer = ({ active, onGetDirections, onAddResource, onAddWaypoint, isNavigating }) => {
  const map = useMap();
  const markerRef = useRef(null);

  useEffect(() => {
    if (!active) {
      if (markerRef.current) { try { map.removeLayer(markerRef.current); } catch {} markerRef.current = null; }
      try { map.getContainer().style.cursor = ''; } catch {}
      return;
    }
    map.getContainer().style.cursor = 'crosshair';

    const handleClick = (e) => {
      const { lat, lng } = e.latlng;
      if (markerRef.current) try { map.removeLayer(markerRef.current); } catch {}

      const icon = L.divIcon({
        html: `<div style="width:20px;height:20px;background:#a855f7;border:3px solid #fff;border-radius:50%;box-shadow:0 0 12px rgba(168,85,247,0.6)"></div>`,
        className: '',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });
      const marker = L.marker([lat, lng], { icon });
      let popup = `<div style="font-family:Manrope,sans-serif;min-width:160px">
        <p style="font-weight:700;font-size:13px;margin:0 0 2px;color:#fff">Dropped Pin</p>
        <p style="font-size:10px;color:#ccc;margin:0 0 6px">${lat.toFixed(5)}, ${lng.toFixed(5)}</p>
        <button class="crisis-directions-btn" data-lat="${lat}" data-lng="${lng}" data-name="Dropped Pin" style="width:100%;padding:5px 0;background:#a855f7;color:#fff;border:none;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer">Navigate here</button>
        <button class="crisis-addresource-btn" data-lat="${lat}" data-lng="${lng}" style="width:100%;padding:5px 0;margin-top:4px;background:#22c55e;color:#fff;border:none;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer">Add Resource here</button>`;
      if (isNavigating) {
        popup += `<button class="crisis-addwaypoint-btn" data-lat="${lat}" data-lng="${lng}" style="width:100%;padding:5px 0;margin-top:4px;background:#3b82f6;color:#fff;border:none;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer">Add Waypoint</button>`;
      }
      popup += `</div>`;
      marker.bindPopup(popup, { className: 'crisis-marker-popup' }).addTo(map).openPopup();
      markerRef.current = marker;
    };

    const handleBtnClick = (e) => {
      const addBtn = e.target.closest('.crisis-addresource-btn');
      if (addBtn && onAddResource) {
        onAddResource({ lat: parseFloat(addBtn.dataset.lat), lng: parseFloat(addBtn.dataset.lng) });
        map.closePopup();
      }
      const wpBtn = e.target.closest('.crisis-addwaypoint-btn');
      if (wpBtn && onAddWaypoint) {
        onAddWaypoint({ lat: parseFloat(wpBtn.dataset.lat), lng: parseFloat(wpBtn.dataset.lng) });
        map.closePopup();
        if (markerRef.current) { try { map.removeLayer(markerRef.current); } catch {} markerRef.current = null; }
      }
    };
    map.getContainer().addEventListener('click', handleBtnClick);

    map.on('click', handleClick);
    return () => {
      try { map.off('click', handleClick); } catch {}
      try { map.getContainer().removeEventListener('click', handleBtnClick); } catch {}
      try { map.getContainer().style.cursor = ''; } catch {}
    };
  }, [active, map, onGetDirections, onAddResource, onAddWaypoint, isNavigating]);

  return null;
};

// Follow User During Navigation — Heading-up with compass + GPS
export const FollowUser = ({ position, active, steps, routeCoords, onUserInteract }) => {
  const map = useMap();
  const currentZoomRef = useRef(17);
  const prevPosRef = useRef(null);
  const lastPanRef = useRef(0);
  const snapIndexRef = useRef(0);
  const pausedRef = useRef(false);
  const pauseTimerRef = useRef(null);
  const stepsRef = useRef(steps);
  const routeCoordsRef = useRef(routeCoords);
  const currentBearingRef = useRef(0);
  const compassActiveRef = useRef(false); // true once compass provides heading

  useEffect(() => { stepsRef.current = steps; }, [steps]);

  const snapToRoute = (loc) => {
    const rc = routeCoordsRef.current;
    if (!rc?.length) return loc;
    const len = rc.length;
    const start = Math.max(0, snapIndexRef.current - 50);
    const end = Math.min(len, snapIndexRef.current + 50);
    let best = loc, bestDist = Infinity, bestIdx = snapIndexRef.current;
    for (let i = start; i < end; i++) {
      const c = rc[i];
      if (!c || !Number.isFinite(c.lat) || !Number.isFinite(c.lng)) continue;
      const d = Math.pow(loc.lat - c.lat, 2) + Math.pow(loc.lng - c.lng, 2);
      if (d < bestDist) { bestDist = d; best = c; bestIdx = i; }
    }
    snapIndexRef.current = bestIdx;
    const distM = Math.sqrt(bestDist) * 111000;
    return distM < 80 ? best : loc;
  };

  // Touch detection — pause auto-follow on any touch
  useEffect(() => {
    if (!active || !map) return;
    const container = map.getContainer();
    if (!container) return;
    const pauseFollow = () => {
      pausedRef.current = true;
      if (onUserInteract) onUserInteract(true);
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = setTimeout(() => {
        pausedRef.current = false;
        if (onUserInteract) onUserInteract(false);
      }, 10000);
    };
    container.addEventListener('touchstart', pauseFollow, { passive: true });
    container.addEventListener('mousedown', pauseFollow);
    return () => {
      container.removeEventListener('touchstart', pauseFollow);
      container.removeEventListener('mousedown', pauseFollow);
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
  }, [map, active, onUserInteract]);

  // Initialize camera with route-based heading ONCE
  // Must wait for routeCoords to be available (arrives after RoutingControl finishes)
  const hasMovedRef = useRef(false);
  const initBearingDoneRef = useRef(false);

  // Sync routeCoords ref and set bearing ONLY on first navigation start (not reroutes)
  useEffect(() => {
    routeCoordsRef.current = routeCoords;
    if (!active || !routeCoords?.length || initBearingDoneRef.current) return;
    const pos = prevPosRef.current || position;
    if (!pos || !Number.isFinite(pos.lat)) return;

    initBearingDoneRef.current = true;
    hasMovedRef.current = false;

    // Compute bearing from user position toward a point ~200m along the route
    let targetPt = routeCoords[Math.min(5, routeCoords.length - 1)];
    for (let i = 1; i < routeCoords.length; i++) {
      const d = Math.sqrt(
        Math.pow((routeCoords[i].lat - pos.lat) * 111000, 2) +
        Math.pow((routeCoords[i].lng - pos.lng) * 111000 * Math.cos(pos.lat * Math.PI / 180), 2)
      );
      if (d > 200) { targetPt = routeCoords[i]; break; }
    }
    const dlat = targetPt.lat - pos.lat;
    const dlng = targetPt.lng - pos.lng;
    const radLat = pos.lat * Math.PI / 180;
    const routeBearing = Math.atan2(dlng * Math.cos(radLat), dlat) * (180 / Math.PI);
    const headingDeg = (routeBearing + 360) % 360;

    if (map.setBearing) {
      if (!map._rotate) { map._rotate = true; map.options.rotate = true; }
      currentBearingRef.current = headingDeg;
      map.setBearing(-headingDeg);
    }
  }, [active, routeCoords, position, map]);

  // Reset flags when navigation ends
  useEffect(() => {
    if (!active) {
      initBearingDoneRef.current = false;
      hasMovedRef.current = false;
      startPosRef.current = null;
      compassActiveRef.current = false;
      isMovingRef.current = false;
    }
  }, [active]);

  // Set start position on mount
  const startPosRef = useRef(null);
  useEffect(() => {
    if (!active || !position || !Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return;
    if (!prevPosRef.current) {
      prevPosRef.current = { lat: position.lat, lng: position.lng };
    }
    if (!startPosRef.current) {
      startPosRef.current = { lat: position.lat, lng: position.lng };
    }
  }, [active, position]);

  // Compass/GPS heading listener — smoothed to prevent fighting during turns
  // FREEZE heading when stopped (car compass unreliable due to metal/electronics)
  const isMovingRef = useRef(false);
  useEffect(() => {
    if (!active) return;
    const SMOOTH_FACTOR = 0.2; // Smooth rotation during turns
    const handler = (e) => {
      const targetHeading = e.detail.heading;
      const speed = e.detail.speed; // m/s, from useHeading
      if (!Number.isFinite(targetHeading)) return;
      compassActiveRef.current = true;
      if (pausedRef.current || !hasMovedRef.current) return;

      // Freeze heading when stopped — car compass is unreliable
      if (speed !== undefined && speed !== null && speed < 2) {
        isMovingRef.current = false;
        return; // Don't change bearing when stopped
      }
      isMovingRef.current = true;

      // Smooth interpolation toward target heading (handles 360° wraparound)
      let diff = targetHeading - currentBearingRef.current;
      if (diff > 180) diff -= 360;
      if (diff < -180) diff += 360;
      if (Math.abs(diff) < 10) return; // 10° dead zone
      const smoothed = (currentBearingRef.current + diff * SMOOTH_FACTOR + 360) % 360;

      if (map.setBearing) {
        if (!map._rotate) { map._rotate = true; map.options.rotate = true; }
        currentBearingRef.current = smoothed;
        map.setBearing(-smoothed);
      }
    };
    window.addEventListener('crisis-heading', handler);
    return () => window.removeEventListener('crisis-heading', handler);
  }, [active, map]);


  // GPS handler — camera follow, heading-up rotation
  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      // Guard: skip if map was destroyed
      try { if (!map || !map.getContainer()) return; } catch { return; }
      const { lat, lng } = e.detail;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      if (pausedRef.current) return;

      const snapped = snapToRoute({ lat, lng });

      // Compute heading from consecutive GPS positions
      if (prevPosRef.current) {
        const dlat = snapped.lat - prevPosRef.current.lat;
        const dlng = snapped.lng - prevPosRef.current.lng;
        const distM = Math.sqrt(dlat * dlat + dlng * dlng) * 111000;
        if (distM < 3) return; // skip micro-jitter

        // Check distance from START position (not just previous)
        // Only switch to GPS heading when user has genuinely left the start area
        const start = startPosRef.current;
        const distFromStart = start ? Math.sqrt(
          Math.pow((snapped.lat - start.lat) * 111000, 2) +
          Math.pow((snapped.lng - start.lng) * 111000 * Math.cos(snapped.lat * Math.PI / 180), 2)
        ) : 0;

        if (!hasMovedRef.current) {
          if (distFromStart < 50) {
            // Still near start — don't change bearing, just update prev for pan/zoom
            prevPosRef.current = { lat: snapped.lat, lng: snapped.lng };
          } else {
            // User has genuinely left the start area
            hasMovedRef.current = true;
          }
        }

        if (hasMovedRef.current && distM > 5 && !compassActiveRef.current) {
          // GPS delta heading — ONLY when compass isn't available
          // Smoothed to prevent jitter during turns
          const radLat = prevPosRef.current.lat * Math.PI / 180;
          const bearing = Math.atan2(
            dlng * Math.cos(radLat),
            dlat
          ) * (180 / Math.PI);
          const targetDeg = (bearing + 360) % 360;

          if (map.setBearing) {
            if (!map._rotate) { map._rotate = true; map.options.rotate = true; }
            // Smooth interpolation (handles 360° wraparound)
            let diff = targetDeg - currentBearingRef.current;
            if (diff > 180) diff -= 360;
            if (diff < -180) diff += 360;
            if (Math.abs(diff) > 10) { // 10° dead zone
              const smoothed = (currentBearingRef.current + diff * 0.25 + 360) % 360;
              currentBearingRef.current = smoothed;
              map.setBearing(-smoothed);
            }
          }
          prevPosRef.current = { lat: snapped.lat, lng: snapped.lng };
        }
      } else {
        prevPosRef.current = { lat: snapped.lat, lng: snapped.lng };
      }

      // Throttle pan/zoom to every 1 second for responsive driving
      const now = Date.now();
      if (now - lastPanRef.current < 1000) return;
      lastPanRef.current = now;

      // Auto-zoom based on distance to nearest turn
      const curSteps = stepsRef.current;
      let nearestTurnDist = Infinity;
      if (curSteps?.length) {
        for (const step of curSteps) {
          if (!step.latLng || step.type === 'WaypointReached' || step.type === 'DestinationReached') continue;
          const d = Math.sqrt(
            Math.pow((snapped.lat - step.latLng.lat) * 111000, 2) +
            Math.pow((snapped.lng - step.latLng.lng) * 111000 * Math.cos(snapped.lat * Math.PI / 180), 2)
          );
          if (d < nearestTurnDist) nearestTurnDist = d;
        }
      }
      let targetZoom;
      if (nearestTurnDist < 40) targetZoom = 18;
      else if (nearestTurnDist < 100) targetZoom = 17.5;
      else if (nearestTurnDist < 250) targetZoom = 17;
      else targetZoom = 16.5;
      if (Math.abs(targetZoom - currentZoomRef.current) >= 0.4) {
        currentZoomRef.current = targetZoom;
        map.setZoom(targetZoom, { animate: true, duration: 0.5 });
      }

      // Position user in lower third of screen — like Google Maps (driver ~65% from top)
      const mapSize = map.getSize();
      const offsetPx = mapSize.y * 0.15;
      const userPoint = map.latLngToContainerPoint([snapped.lat, snapped.lng]);
      const offsetCenter = map.containerPointToLatLng([userPoint.x, userPoint.y - offsetPx]);
      map.panTo(offsetCenter, { animate: true, duration: 0.8, easeLinearity: 0.5 });
    };
    window.addEventListener('crisis-gps', handler);
    return () => window.removeEventListener('crisis-gps', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, active]);

  // Re-center handler
  useEffect(() => {
    const handler = () => {
      pausedRef.current = false;
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
      if (onUserInteract) onUserInteract(false);
      // Restore current bearing on recenter
      if (map.setBearing) map.setBearing(-currentBearingRef.current);
      if (prevPosRef.current) {
        const s = prevPosRef.current;
        const mapSize = map.getSize();
        const offsetPx = mapSize.y * 0.15;
        const userPoint = map.latLngToContainerPoint([s.lat, s.lng]);
        const offsetCenter = map.containerPointToLatLng([userPoint.x, userPoint.y - offsetPx]);
        map.setView(offsetCenter, currentZoomRef.current, { animate: true, duration: 0.3 });
      }
    };
    window.addEventListener('crisis-recenter', handler);
    return () => window.removeEventListener('crisis-recenter', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, onUserInteract]);

  // Reset bearing to North when navigation ends
  useEffect(() => {
    return () => {
      try { if (map?.setBearing) map.setBearing(0); } catch {}
    };
  }, [map, active]);

  return null;
};

// Destination Pin Marker
export const DestinationMarker = ({ position, name }) => {
  if (!position || !Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return null;
  const icon = L.divIcon({
    html: `<div style="width:24px;height:24px;background:#a855f7;border:3px solid #fff;border-radius:50%;box-shadow:0 0 16px rgba(168,85,247,0.7),0 0 30px rgba(168,85,247,0.3)"></div>`,
    className: '',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
  return <Marker position={[position.lat, position.lng]} icon={icon} />;
};


// UK city markers for city-to-city navigation
const UK_CITIES = [
  { name: 'London', lat: 51.5074, lng: -0.1278 },
  { name: 'Birmingham', lat: 52.4862, lng: -1.8904 },
  { name: 'Manchester', lat: 53.4808, lng: -2.2426 },
  { name: 'Leeds', lat: 53.8008, lng: -1.5491 },
  { name: 'Liverpool', lat: 53.4084, lng: -2.9916 },
  { name: 'Newcastle', lat: 54.9783, lng: -1.6178 },
  { name: 'Sheffield', lat: 53.3811, lng: -1.4701 },
  { name: 'Bristol', lat: 51.4545, lng: -2.5879 },
  { name: 'Nottingham', lat: 52.9548, lng: -1.1581 },
  { name: 'Leicester', lat: 52.6369, lng: -1.1398 },
  { name: 'Edinburgh', lat: 55.9533, lng: -3.1883 },
  { name: 'Glasgow', lat: 55.8642, lng: -4.2518 },
  { name: 'Cardiff', lat: 51.4816, lng: -3.1791 },
  { name: 'Belfast', lat: 54.5973, lng: -5.9301 },
  { name: 'Brighton', lat: 50.8225, lng: -0.1372 },
  { name: 'Southampton', lat: 50.9097, lng: -1.4044 },
  { name: 'Plymouth', lat: 50.3755, lng: -4.1427 },
  { name: 'Coventry', lat: 52.4068, lng: -1.5197 },
  { name: 'Derby', lat: 52.9225, lng: -1.4746 },
  { name: 'Wolverhampton', lat: 52.5870, lng: -2.1288 },
  { name: 'Aberdeen', lat: 57.1497, lng: -2.0943 },
  { name: 'Swansea', lat: 51.6214, lng: -3.9436 },
  { name: 'Oxford', lat: 51.7520, lng: -1.2577 },
  { name: 'Cambridge', lat: 52.2053, lng: 0.1218 },
  { name: 'York', lat: 53.9591, lng: -1.0815 },
  { name: 'Bath', lat: 51.3811, lng: -2.3590 },
  { name: 'Dundee', lat: 56.4620, lng: -2.9707 },
  { name: 'Inverness', lat: 57.4778, lng: -4.2247 },
  { name: 'Exeter', lat: 50.7184, lng: -3.5339 },
  { name: 'Norwich', lat: 52.6309, lng: 1.2974 },
];

// 32 London boroughs + City of London
const LONDON_BOROUGHS = [
  { name: 'Barking & Dagenham', lat: 51.5397, lng: 0.1313 },
  { name: 'Barnet', lat: 51.6252, lng: -0.1517 },
  { name: 'Bexley', lat: 51.4549, lng: 0.1505 },
  { name: 'Brent', lat: 51.5588, lng: -0.2817 },
  { name: 'Bromley', lat: 51.4039, lng: 0.0198 },
  { name: 'Camden', lat: 51.5290, lng: -0.1255 },
  { name: 'City of London', lat: 51.5155, lng: -0.0922 },
  { name: 'Croydon', lat: 51.3762, lng: -0.0982 },
  { name: 'Ealing', lat: 51.5130, lng: -0.3089 },
  { name: 'Enfield', lat: 51.6538, lng: -0.0799 },
  { name: 'Greenwich', lat: 51.4892, lng: 0.0648 },
  { name: 'Hackney', lat: 51.5450, lng: -0.0553 },
  { name: 'Hammersmith & Fulham', lat: 51.4927, lng: -0.2339 },
  { name: 'Haringey', lat: 51.6000, lng: -0.1119 },
  { name: 'Harrow', lat: 51.5898, lng: -0.3346 },
  { name: 'Havering', lat: 51.5779, lng: 0.2121 },
  { name: 'Hillingdon', lat: 51.5441, lng: -0.4760 },
  { name: 'Hounslow', lat: 51.4746, lng: -0.3680 },
  { name: 'Islington', lat: 51.5465, lng: -0.1058 },
  { name: 'Kensington & Chelsea', lat: 51.4990, lng: -0.1938 },
  { name: 'Kingston upon Thames', lat: 51.4085, lng: -0.3064 },
  { name: 'Lambeth', lat: 51.4607, lng: -0.1163 },
  { name: 'Lewisham', lat: 51.4535, lng: -0.0205 },
  { name: 'Merton', lat: 51.4098, lng: -0.1949 },
  { name: 'Newham', lat: 51.5077, lng: 0.0469 },
  { name: 'Redbridge', lat: 51.5590, lng: 0.0741 },
  { name: 'Richmond upon Thames', lat: 51.4613, lng: -0.3037 },
  { name: 'Southwark', lat: 51.5035, lng: -0.0804 },
  { name: 'Sutton', lat: 51.3618, lng: -0.1945 },
  { name: 'Tower Hamlets', lat: 51.5099, lng: -0.0059 },
  { name: 'Waltham Forest', lat: 51.5886, lng: -0.0118 },
  { name: 'Wandsworth', lat: 51.4571, lng: -0.1818 },
  { name: 'Westminster', lat: 51.4975, lng: -0.1357 },
];

export const CityMarkers = ({ onNavigate, visible }) => {
  const map = useMap();
  const layerRef = useRef(null);
  const boroughLayerRef = useRef(null);

  useEffect(() => {
    if (layerRef.current) { try { map.removeLayer(layerRef.current); } catch {} layerRef.current = null; }
    if (boroughLayerRef.current) { try { map.removeLayer(boroughLayerRef.current); } catch {} boroughLayerRef.current = null; }
    if (!visible) return;

    const cityGroup = L.layerGroup();
    const boroughGroup = L.layerGroup();

    UK_CITIES.forEach(city => {
      const icon = L.divIcon({
        html: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap;pointer-events:auto">
          <div style="width:10px;height:10px;border-radius:50%;background:#facc15;border:2px solid rgba(0,0,0,0.3);box-shadow:0 0 6px rgba(250,204,21,0.5)"></div>
          <span style="font-size:11px;font-weight:700;color:#1a1a2e;text-shadow:0 0 3px rgba(255,255,255,0.8),0 0 6px rgba(255,255,255,0.4);letter-spacing:0.5px">${city.name}</span>
        </div>`,
        className: '',
        iconSize: [0, 0],
        iconAnchor: [-6, 5],
      });
      const marker = L.marker([city.lat, city.lng], { icon });
      marker.on('click', () => onNavigate?.({ lat: city.lat, lng: city.lng, name: city.name }));
      cityGroup.addLayer(marker);
    });

    LONDON_BOROUGHS.forEach(borough => {
      const icon = L.divIcon({
        html: `<div style="display:flex;align-items:center;gap:3px;white-space:nowrap;pointer-events:auto">
          <div style="width:8px;height:8px;border-radius:50%;background:#38bdf8;border:2px solid rgba(0,0,0,0.2);box-shadow:0 0 4px rgba(56,189,248,0.5)"></div>
          <span style="font-size:10px;font-weight:600;color:#1e293b;text-shadow:0 0 3px rgba(255,255,255,0.9),0 0 5px rgba(255,255,255,0.5);letter-spacing:0.3px">${borough.name}</span>
        </div>`,
        className: '',
        iconSize: [0, 0],
        iconAnchor: [-5, 4],
      });
      const marker = L.marker([borough.lat, borough.lng], { icon });
      marker.on('click', () => onNavigate?.({ lat: borough.lat, lng: borough.lng, name: borough.name }));
      boroughGroup.addLayer(marker);
    });

    layerRef.current = cityGroup;
    boroughLayerRef.current = boroughGroup;

    // Show/hide boroughs and cities based on zoom level
    const updateVisibility = () => {
      const zoom = map.getZoom();
      if (zoom >= 10) {
        if (!map.hasLayer(boroughGroup)) boroughGroup.addTo(map);
      } else {
        if (map.hasLayer(boroughGroup)) try { map.removeLayer(boroughGroup); } catch {}
      }
      // Hide city names at very wide zooms to reduce clutter
      if (zoom >= 6) {
        if (!map.hasLayer(cityGroup)) cityGroup.addTo(map);
      } else {
        if (map.hasLayer(cityGroup)) try { map.removeLayer(cityGroup); } catch {}
      }
    };
    updateVisibility();
    map.on('zoomend', updateVisibility);

    return () => {
      try { map.off('zoomend', updateVisibility); } catch {}
      try { if (map.hasLayer(cityGroup)) map.removeLayer(cityGroup); } catch {}
      try { if (map.hasLayer(boroughGroup)) map.removeLayer(boroughGroup); } catch {}
    };
  }, [map, visible, onNavigate]);

  return null;
};

// Fuel Station Markers Layer
export const FuelStationMarkers = ({ stations, visible, onNavigate }) => {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (layerRef.current) { try { map.removeLayer(layerRef.current); } catch {} layerRef.current = null; }
    if (!visible || !stations?.length) return;

    const group = L.markerClusterGroup({
      maxClusterRadius: 40,
      disableClusteringAtZoom: 12,
      spiderfyOnMaxZoom: true,
      zIndexOffset: 1000,
      iconCreateFunction: (cluster) => L.divIcon({
        html: `<div style="width:36px;height:36px;background:linear-gradient(135deg,#ef4444,#f97316);border:3px solid rgba(255,255,255,0.9);border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:12px;font-weight:700;box-shadow:0 3px 12px rgba(239,68,68,0.5);z-index:1000">${cluster.getChildCount()}</div>`,
        className: '',
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      }),
    });

    stations.forEach(station => {
      if (!station.lat || !station.lng) return;
      const icon = L.divIcon({
        html: `<div style="width:28px;height:28px;display:flex;align-items:center;justify-content:center;background:#1a1a2e;border:2px solid #ef4444;border-radius:6px;box-shadow:0 2px 8px rgba(239,68,68,0.35)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 22V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v17"/><path d="M13 10h4a2 2 0 0 1 2 2v5a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 4"/><path d="M3 22h10"/><path d="M7 8h2"/><path d="M7 12h2"/></svg>
        </div>`,
        className: '',
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });

      const brand = station.brand || station.source || '';
      const unleaded = station.unleaded ? `${station.unleaded}p` : '—';
      const diesel = station.diesel ? `${station.diesel}p` : '—';
      const premium = station.super_unleaded ? `${station.super_unleaded}p` : '';

      const popup = `<div style="font-family:Manrope,sans-serif;min-width:170px;padding:2px">
        <p style="font-weight:700;font-size:13px;margin:0 0 2px;color:#fff">${brand}</p>
        <p style="font-size:10px;color:#ccc;margin:0 0 6px">${station.address || station.postcode || ''}</p>
        <div style="display:flex;gap:8px;margin-bottom:8px">
          <div style="text-align:center"><span style="font-size:9px;color:#999;display:block">Unleaded</span><span style="font-size:13px;font-weight:700;color:#22c55e">${unleaded}</span></div>
          <div style="text-align:center"><span style="font-size:9px;color:#999;display:block">Diesel</span><span style="font-size:13px;font-weight:700;color:#3b82f6">${diesel}</span></div>
          ${premium ? `<div style="text-align:center"><span style="font-size:9px;color:#999;display:block">Super</span><span style="font-size:13px;font-weight:700;color:#f59e0b">${premium}</span></div>` : ''}
        </div>
        <button class="crisis-directions-btn" data-lat="${station.lat}" data-lng="${station.lng}" data-name="${brand} ${station.address || ''}" style="width:100%;padding:5px 0;background:#ef4444;color:#fff;border:none;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer">Navigate</button>
      </div>`;

      const marker = L.marker([station.lat, station.lng], { icon, zIndexOffset: 1000 });
      marker.bindPopup(popup, { className: 'crisis-marker-popup' });
      group.addLayer(marker);
    });

    group.addTo(map);
    layerRef.current = group;

    return () => {
      try { if (layerRef.current) map.removeLayer(layerRef.current); } catch {}
    };
  }, [map, visible, stations, onNavigate]);

  return null;
};



// Incident markers — shows reported incidents on the map
const INCIDENT_ICONS = {
  fire: { emoji: '\uD83D\uDD25', color: '#ef4444' },
  flood: { emoji: '\uD83C\uDF0A', color: '#3b82f6' },
  power_outage: { emoji: '\u26A1', color: '#eab308' },
  road_accident: { emoji: '\uD83D\uDE97', color: '#f97316' },
  violence: { emoji: '\uD83D\uDEE1\uFE0F', color: '#8b5cf6' },
  medical: { emoji: '\u2764\uFE0F', color: '#ec4899' },
  other: { emoji: '\u26A0\uFE0F', color: '#6b7280' },
};

const SEVERITY_BORDER = {
  low: '#22c55e',
  medium: '#eab308',
  high: '#f97316',
  critical: '#ef4444',
};

export const IncidentMarkers = ({ incidents, visible = true, onVote }) => {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (layerRef.current) {
      try { map.removeLayer(layerRef.current); } catch {}
      layerRef.current = null;
    }
    if (!visible || !incidents?.length) return;

    const group = L.layerGroup();

    incidents.forEach(inc => {
      if (!Number.isFinite(inc.lat) || !Number.isFinite(inc.lng)) return;
      const iconData = INCIDENT_ICONS[inc.type] || INCIDENT_ICONS.other;
      const borderColor = SEVERITY_BORDER[inc.severity] || '#6b7280';
      const ups = inc.upvotes || 0;
      const downs = inc.downvotes || 0;
      const confidence = ups + downs > 0 ? Math.round((ups / (ups + downs)) * 100) : 100;

      const icon = L.divIcon({
        html: `<div style="
          width:32px;height:32px;border-radius:50%;
          background:${iconData.color};
          border:3px solid ${borderColor};
          display:flex;align-items:center;justify-content:center;
          font-size:16px;box-shadow:0 2px 8px rgba(0,0,0,0.4);
          animation: incidentPulse 2s ease-in-out infinite;
        ">${iconData.emoji}</div>`,
        className: 'incident-marker-icon',
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });

      const marker = L.marker([inc.lat, inc.lng], { icon, zIndexOffset: 900 });

      const timeAgo = inc.created_at ? getTimeAgo(inc.created_at) : '';
      const typeLabel = (inc.type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const sevLabel = (inc.severity || 'medium').replace(/\b\w/g, c => c.toUpperCase());
      const iid = inc.incident_id;

      marker.bindPopup(
        `<div style="font-family:system-ui;min-width:160px" data-testid="incident-popup-${iid}">
          <p style="margin:0 0 4px;font-weight:700;font-size:13px;color:#fff">${iconData.emoji} ${typeLabel}</p>
          <p style="margin:0 0 2px;font-size:11px;color:#ccc">Severity: <strong style="color:${borderColor}">${sevLabel}</strong></p>
          ${inc.description ? `<p style="margin:0 0 2px;font-size:11px;color:#bbb">${inc.description}</p>` : ''}
          <p style="margin:0 0 4px;font-size:10px;color:#888">${inc.reported_by_name || 'Anonymous'} &middot; ${timeAgo}</p>
          <div style="display:flex;align-items:center;gap:6px;margin:0 0 6px;font-size:10px;color:#aaa">
            <span style="color:#22c55e">${ups} yes</span> &middot; <span style="color:#ef4444">${downs} no</span> &middot; ${confidence}% say still there
          </div>
          <p style="margin:0 0 6px;font-size:11px;font-weight:600;color:#fff">Still there?</p>
          <div style="display:flex;gap:6px">
            <button onclick="window.dispatchEvent(new CustomEvent('incident-vote',{detail:{id:'${iid}',vote:'yes'}}))"
              style="flex:1;padding:6px 0;border-radius:8px;border:none;background:#22c55e;color:#fff;font-size:11px;font-weight:600;cursor:pointer">
              Yes
            </button>
            <button onclick="window.dispatchEvent(new CustomEvent('incident-vote',{detail:{id:'${iid}',vote:'no'}}))"
              style="flex:1;padding:6px 0;border-radius:8px;border:none;background:#ef4444;color:#fff;font-size:11px;font-weight:600;cursor:pointer">
              Not anymore
            </button>
          </div>
        </div>`,
        { className: 'crisis-marker-popup', maxWidth: 220 }
      );

      group.addLayer(marker);
    });

    group.addTo(map);
    layerRef.current = group;

    return () => {
      try { if (layerRef.current) map.removeLayer(layerRef.current); } catch {}
    };
  }, [map, visible, incidents]);

  // Listen for vote events from popup buttons
  useEffect(() => {
    const handler = (e) => {
      const { id, vote } = e.detail || {};
      if (id && vote && onVote) onVote(id, vote);
    };
    window.addEventListener('incident-vote', handler);
    return () => window.removeEventListener('incident-vote', handler);
  }, [onVote]);

  return null;
};

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
